import { getLocalTimeZone } from "@internationalized/date";
import { TrainIdentifier } from "@vygruppen/vy-train-map";
import {
  GroupTrainForm,
  ProductionDeviationForm,
} from "features/CenterContent/RoleContent/ProductionDeviation/types/formSchema";
import { defaultForm } from "features/CenterContent/RoleContent/ProductionDeviation/types/transformers";
import { TrainListRange } from "features/CenterContent/RoleContent/ProductionDeviation/useTrainListRange";
import { TimedFormDuration } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import { useProductionDeviation } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/useProductionDeviation";
import { OperationalInformationTypes } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { TrainInfoBatchFormProps } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoBatchForm";
import { TrainFormGroup } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { TrainInformationDetailedResponse } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/responseSchema";
import {
  baseDefaultFormSchema,
  trainDetailedResponseToForm,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/transformers";
import { toTrainSerie } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/utils";
import {
  getDirection,
  getDirectionText,
} from "features/VehicleSidebar/AffectedTrains/utils/utils";
import { groupBy } from "features/VehicleSidebar/VehicleList/TrainList/TrainList";
import { TrainData } from "features/VehicleSidebar/VehicleList/TrainList/types";
import { useEffect, useState } from "react";
import {
  DefaultValues,
  FieldPath,
  useFormContext,
  UseFormReturn,
} from "react-hook-form";
import { partition } from "shared/utils/arrayUtils";
import { calendarDateTimeToDate } from "shared/utils/dateUtils";
import { css } from "styled-components";

export const toTrainId = ({
  trainNumber,
  trainDate,
  countryCode,
}: TrainData) => ({
  identifier: trainNumber,
  nominalDate: trainDate,
  countryCode,
});

export const groupIdsBySeries = (trainIds: TrainIdentifier[]) =>
  groupBy(trainIds, ({ identifier }) => toTrainSerie(identifier));

export const groupTrainsBySeries = (trainList: TrainData[]) =>
  groupBy(trainList, (train) => toTrainSerie(train.trainNumber));

export const productionDeviationGroupPath = (trainSerie: string) =>
  `productionDeviationForm.${trainSerie}` as const;

export const productionDeviationPath = (trainSerie: string) => ({
  path: <T extends FieldPath<GroupTrainForm>>(field: T) =>
    `${productionDeviationGroupPath(trainSerie)}.${field}` as const,
});

export const useProductionDeviationPath = productionDeviationPath;
export type Path = ReturnType<typeof useProductionDeviationPath>["path"];

export const getAllRepresentativeTrains = (trainIds: TrainIdentifier[]) => {
  const groupedData = groupIdsBySeries(trainIds);
  const representativeTrainIds = Object.values(groupedData)
    .map((group) =>
      partition(group, (it) => getDirection(it) === "ODD").map((it) => it[0]),
    )
    .flat()
    .filter(Boolean);
  return representativeTrainIds;
};

export const filterDurations = (
  trainList: TrainData[],
  durations: TimedFormDuration,
) => {
  const startDateTime = durations.from_time.toDate(getLocalTimeZone());
  const endDateTime = durations.to_time.toDate(getLocalTimeZone());

  if (startDateTime >= endDateTime) {
    return [];
  }

  const trainListFiltered = trainList.filter(
    (it) =>
      it.destinationScheduledArrivalTime &&
      it.destinationScheduledArrivalTime > startDateTime &&
      it.originScheduledDepartureTime &&
      it.originScheduledDepartureTime < endDateTime,
  );
  const trainIdsFiltered = trainListFiltered.map(toTrainId);
  return trainIdsFiltered.map((it) => it.identifier);
};

/**
 * Recreation of createInitialBatchFormStateFromTrainSeries but for ProductionDeviation
 */
export const createInitialBatchFormState = (
  trainSeries: string,
): DefaultValues<TrainFormGroup>[] => {
  const directions = ["ODD", "EVEN"] as const;
  return directions.map((direction) => ({
    trainSeries,
    groupLabel: trainSeries,
    route: "ROUTE",
    direction,
    trainIdsInGroup: [],
    trainForm: baseDefaultFormSchema(),
  }));
};

export const createBatchFormStateFromResponse = (
  trainSeries: string,
  trainEvents: TrainInformationDetailedResponse[],
): DefaultValues<TrainFormGroup>[] => {
  const groups = groupBy(trainEvents, (it) => getDirection(it.trainIdentifier));
  const directions = ["ODD", "EVEN"] as const;
  return directions.map((direction, i) => {
    const trainEventsGroup = groups[direction] ?? [];
    const otherDirection = directions[1 - i];
    const trainEventsOtherGroup = groups[otherDirection] ?? [];
    const representativeTrainEvent =
      trainEventsGroup[0] ?? trainEventsOtherGroup[0]; // We prefer a train from this group, but pick from the other group if necessary
    return {
      trainSeries,
      groupLabel: trainSeries,
      route: "ROUTE",
      direction,
      trainIdsInGroup: trainEventsGroup.map((it) => it.trainIdentifier),
      trainForm: representativeTrainEvent
        ? trainDetailedResponseToForm(representativeTrainEvent)
        : baseDefaultFormSchema(),
    };
  });
};

export const makeBatchProps = (
  path: Path,
  opInfoTypes: OperationalInformationTypes,
  mode: "create" | "edit",
): TrainInfoBatchFormProps => ({
  infrastructureEvent: undefined,
  groupsInSeries: 2,
  activeTrainSeriesKey: path("batchTrainForm") as `\${groupKey}`,
  opInfoTypes,
  mode,
});

/**
 * Done like this since we need to update the batchTrainForm at the same time as trainSeries is set
 */
export const useTrainSeries = () => {
  const { reset, getValues } = useFormContext<ProductionDeviationForm>();
  const productionDeviationForm = getValues("productionDeviationForm") ?? {};
  const trainSeriesForm = Object.keys(productionDeviationForm);
  const [trainSeries, setTrainSeries] = useState<string[]>(trainSeriesForm);
  const [prevTrainSeries, setPrevTrainSeries] =
    useState<string[]>(trainSeriesForm);
  useEffect(() => {
    // Without this check a single refresh from editing code will reset the form, which is hard to work with
    if (
      trainSeries.length === 0 ||
      JSON.stringify(trainSeries) !== JSON.stringify(prevTrainSeries)
    ) {
      reset(defaultForm(trainSeries));
      setPrevTrainSeries([...trainSeries]);
    }
  }, [trainSeries]);
  return [trainSeries, setTrainSeries] as const;
};

/**
 * Done like this since we need to update the batchTrainForm at the same time as selectedTrains is set
 */
export const useSelectedTrains = (
  trainSerie: string,
  trainList: TrainListRange["trainList"],
) => {
  const { path } = useProductionDeviationPath(trainSerie);
  const { setValue, watch } = useFormContext<ProductionDeviationForm>();

  const selectedTrains = watch(path("selectedTrains")) ?? [];
  const [_selectedTrains, setSelectedTrains] =
    useState<string[]>(selectedTrains);
  useEffect(() => {
    setValue(path("selectedTrains"), _selectedTrains);

    // We pick and select trains using only their identifier and add countryCode and
    // nominalDate at the end to simplify the code at SelectTrainIds.tsx.
    // An empty trainList means no trains run in the selected period, so there is
    // nothing to map and no train to read countryCode/nominalDate from.
    const anyTrain = trainList[0];
    if (!anyTrain) return;
    const { countryCode, trainDate: nominalDate } = anyTrain;

    ["ODD", "EVEN"].forEach((dir, index) =>
      setValue(
        path(`batchTrainForm.${index}.trainIdsInGroup`),
        _selectedTrains
          .filter((identifier) => getDirectionText(identifier) === dir)
          .map(
            (identifier: string): TrainIdentifier => ({
              identifier,
              nominalDate,
              countryCode,
            }),
          ),
      ),
    );
  }, [_selectedTrains]);
  return [selectedTrains, setSelectedTrains] as const;
};

export const PANEL_STYLE = css`
  background-color: ${({ theme }) => theme.colorBackgroundSecondary};
  padding: 18px;
  border-radius: 18px;
`;

export const triggerTypeAndReason = async (
  formContext: UseFormReturn<ProductionDeviationForm>,
  path: Path,
) => {
  const { getFieldState, trigger } = formContext;
  await trigger();
  // Do a check for type and reason only, as the rest of trainForm is filled in the next step
  const type = getFieldState(path(`batchTrainForm.0.trainForm.type`));
  const reason = getFieldState(path(`batchTrainForm.0.trainForm.reason`));
  return !type.error && !reason.error;
};

export const triggerSkipEmptyDirections = async (
  formContext: UseFormReturn<ProductionDeviationForm>,
  path: Path,
) => {
  const { watch, getFieldState, trigger } = formContext;

  await trigger();
  const batchTrainForm = watch(path(`batchTrainForm`));

  return batchTrainForm.every((form, index) => {
    // Skip validation for empty train groups
    if (form.trainIdsInGroup.length === 0) return true;

    // Check if this direction has validation errors
    const fieldState = getFieldState(path(`batchTrainForm.${index}`));
    return !fieldState.error;
  });
};

export const submitSkipEmptyDirections = async (
  formContext: UseFormReturn<ProductionDeviationForm>,
  onSubmit: () => Promise<void>,
) => {
  const { getValues, setValue, trigger } = formContext;

  // Fix the form data first
  const currentValues = getValues();

  const batchTrainForms = currentValues.productionDeviationForm;
  Object.keys(batchTrainForms).forEach((trainSerie) => {
    const batchTrainForm = batchTrainForms[trainSerie].batchTrainForm.filter(
      (it) => it.trainIdsInGroup.length > 0,
    );
    setValue(
      `productionDeviationForm.${trainSerie}.batchTrainForm`,
      batchTrainForm,
    );
  });

  // Trigger validation and submission
  await trigger(); // re-validate after fixing

  void onSubmit();
};

export const substitudePreviewText = (previewText: string, trainId: string) => {
  const substitution = "[Gjeldende tog]";
  return previewText
    .replaceAll(`Tog ${trainId}`, substitution)
    .replaceAll(trainId, substitution);
};

/**
 * We retrieve every productionDeviation (even those outside of this train serie),
 * but it is the most lightweight/easy approach since production deviations by themselves
 * are just metadata comparable to retrieving a small list of strings.
 * Thus instead of making e.g. a new endpoint for this, just check through these metadata.
 */
export const useTrainsAlreadyInProductionDeviation = (
  trainSerie: string,
  durations: TimedFormDuration,
) => {
  const productionDeviations = useProductionDeviation();
  if (!productionDeviations.data) return [];
  const productionDeviationThisSerie = productionDeviations.data.filter(
    (it) =>
      it.trainIds.length > 0 && toTrainSerie(it.trainIds[0]) === trainSerie,
  );
  const productionDeviationThisPeriod = productionDeviationThisSerie.filter(
    (it) =>
      it.fromDate.toDate().getTime() <=
        calendarDateTimeToDate(durations.to_time).getTime() &&
      it.toDate.toDate().getTime() >=
        calendarDateTimeToDate(durations.from_time).getTime(),
  );
  return productionDeviationThisPeriod?.flatMap((it) => it.trainIds);
};
